"use client";

import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CloseIcon } from "@/components/icons";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useGetAssignmentAttemptDetailQuery } from "@/api/assignment/assignmentApi";
import { useParams } from "next/navigation";
import type { AiReportType } from "@/api/reports/reportApi";
import { QDensityChartBlock } from "./blocks/QDensityChartBlock";
import { ActionsChartBlock } from "./blocks/ActionsChartBlock";
import { TranscribeChartBlock } from "./blocks/TranscribeChartBlock";
import type { AIChartsPanelModalProps } from "./types";
import type { AiAnalyticsData } from "./blocks/types";
import { extractSummaryText, getLocalizedSummary } from "../../../utils/summaryHelpers";
import { Card, CardContent } from "@/components/ui/card";

import { useIsMobile } from "@/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type TabValue = "qDensity" | "actions" | "transcribe";

interface TabConfig {
  value: TabValue;
  labelKey: string;
  content: (attemptId: number) => ReactNode;
}

const TAB_TRIGGER_CLASSNAME = "flex items-center rounded-sm p-2! text-sm font-medium data-[state=active]:bg-[#0059FF] data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=inactive]:bg-transparent data-[state=inactive]:text-muted-foreground";
const TAB_CONTENT_CLASSNAME = "mt-0 flex-none overflow-visible data-[state=active]:block data-[state=inactive]:hidden";

const getDefaultTab = (tab?: AiReportType): TabValue => {
  if (!tab) return "qDensity";
  if (tab === "evaluation") return "qDensity";
  return tab as TabValue;
};

const helpContent = {
  qDensity: {
    title: "Справка: Плотность вопросов студента",
    sections: [
      {
        title: "Что показывает график Плотности вопросов студента?",
        content:
          "График отражает частоту и плотность вопросов студента в ходе занятия с разбивкой по минутам.\n\nЗначения формируются на основе активности вопросов и их плотности во времени.",
      },
      {
        title: "Как работать с графиком?",
        content:
          "— Используйте масштаб «по минутам» для анализа конкретных моментов\n\n— Наводите курсор для просмотра точных значений\n\n— Обращайте внимание на отмеченные подозрительные интервалы",
      },
      {
        title: "Как интерпретировать?",
        content:
          "— Плавные колебания → естественный темп работы\n\n— Резкие пики → возможная внешняя помощь или несоразмерная активность\n\n— Повторяющиеся пики → повод для дополнительной проверки",
      },
    ],
  },
  actions: {
    title: "Справка: Активность по логам",
    sections: [
      {
        title: "Что показывает график Активности по логам?",
        content:
          "Отражает зафиксированные системные и поведенческие события (шум, расфокус камеры, выход из полноэкранного режима и др.).",
      },
      {
        title: "Как работать с графиком?",
        content:
          "— Смотрите список событий и их долю\n\n— Используйте кольцевую диаграмму для оценки распределения\n\n— Переходите к логам для детального анализа",
      },
      {
        title: "Как интерпретировать?",
        content:
          "— Преобладание технических событий → возможные отвлечения\n\n— Повторяющиеся нарушения → риск несоблюдения регламента\n\n— Разовые события → допустимы и не критичны",
      },
    ],
  },
  transcribe: {
    title: "Справка: Анализ транскрипции речи",
    sections: [
      {
        title: "Что показывает график Анализ транскрипции речи?",
        content:
          "Анализирует речевую активность студента: количество речевых сегментов и их общую длительность.",
      },
      {
        title: "Как работать с графиком?",
        content:
          "— Сравнивайте длительность речи с форматом занятия\n\n— Обращайте внимание на флаг «возможное нарушение»",
      },
      {
        title: "Как интерпретировать?",
        content:
          "— Умеренная речь → нормальное участие\n\n— Чрезмерная или несоразмерная активность → возможное вмешательство третьих лиц\n\n— Несоответствие контексту занятия → требует проверки",
      },
    ],
  },
};

const AIChartsPanelModal = memo(function AIChartsPanelModal({
  open,
  attemptId,
  contextId,
  initialTab,
  tabDisabled,
  onClose,
  onOpenTranscribeDetail,
  questionResults,
  entity,
  filteredComponents,
  assignmentType,
  webinarRecordsOverride,
  resultsChartOverride,
  onlyTab,
  credibilityVerdict,
}: AIChartsPanelModalProps) {
  const t = useTranslations();
  const locale = useLocale();
  const isMobile = useIsMobile();
  const params = useParams();
  const assignmentId = params.id ? Number(params.id) : null;
  const isExternalAssignment = assignmentType === "external";

  const resolveNextEnabledTab = useCallback(
    (preferred: TabValue): TabValue => {
      const isHidden = (tab: TabValue) =>
        !!tabDisabled?.[tab] || (tab === "qDensity" && isExternalAssignment);
      if (!isHidden(preferred)) return preferred;
      const fallback: TabValue[] = ["qDensity", "actions", "transcribe"];
      return fallback.find((tab) => !isHidden(tab)) ?? preferred;
    },
    [tabDisabled, isExternalAssignment]
  );

  const [activeTab, setActiveTab] = useState<TabValue>(() =>
    resolveNextEnabledTab(getDefaultTab(initialTab))
  );
  const [showHelp, setShowHelp] = useState(false);
  const [qDensityAnalytics, setQDensityAnalytics] = useState<AiAnalyticsData | null>(null);
  const [actionsAnalytics, setActionsAnalytics] = useState<AiAnalyticsData | null>(null);
  const [transcribeAnalytics, setTranscribeAnalytics] = useState<AiAnalyticsData | null>(null);

  const getSummaryText = (): string => {
    let currentAnalytics: AiAnalyticsData | null = null;

    switch (activeTab) {
      case 'qDensity':
        currentAnalytics = qDensityAnalytics;
        break;
      case 'actions':
        currentAnalytics = actionsAnalytics;
        break;
      case 'transcribe':
        currentAnalytics = transcribeAnalytics;
        break;
      default:
        return '';
    }

    if (!currentAnalytics) return '';

    // summary_* приходит и как строка (public-эндпоинт), и как { content } (SSE-стрим) —
    // extractSummaryText разбирает оба формата.
    const localized = getLocalizedSummary(currentAnalytics, locale);
    return extractSummaryText(localized ?? undefined);
  };

  const { data: assignmentData } = useGetAssignmentAttemptDetailQuery(
    {
      assignmentId: assignmentId!,
      contextId: contextId!,
      attemptId: attemptId!
    },
    {
      skip: !assignmentId || !contextId || !attemptId || !open,
    }
  );

  const webinarRecords = webinarRecordsOverride ?? assignmentData?.webinar_records;

  const handleClose = () => {
    setShowHelp(false);
    onClose();
  };

  const resultsChart =
    resultsChartOverride ??
    (assignmentData?.results_chart as unknown as
      | Array<{ name: string; data: number[] }>
      | undefined);


  const tabs: TabConfig[] = useMemo(
    () => [
      {
        value: "qDensity",
        labelKey: "label-answers-report-tab",
        content: (attemptId) =>
          assignmentType === "external" ? (
            <Card className="w-full bg-white flex flex-col p-4 gap-4">
              <CardContent className="p-0">
                <div className="flex items-center justify-center text-muted-foreground text-sm h-[150px]">
                  {t("label-sdk-answer-intensity-unavailable")}
                </div>
              </CardContent>
            </Card>
          ) : (
            <QDensityChartBlock
              attemptId={attemptId}
              assignmentReportData={{
                results_chart: resultsChart,
              }}
              assignmentId={assignmentId!}
              contextId={contextId}
              onAnalyticsData={setQDensityAnalytics}
              // Используем переданные данные для синхронизации с ResultsTab
              questionResults={questionResults}
              entity={entity}
              filteredComponents={filteredComponents}
            />
          ),
      },
      {
        value: "actions",
        labelKey: "label-actions-report-tab",
        // «Активность по логам» = НАШ credibility_verdict из Laravel (передаём как verdict).
        // Без verdict — фолбэк на AI-отчёт actions. Запросов к rating-сервису с verdict нет.
        content: (attemptId) => (
          <ActionsChartBlock
            attemptId={attemptId}
            verdict={credibilityVerdict}
            onAnalyticsData={setActionsAnalytics}
          />
        ),
      },
      {
        value: "transcribe",
        labelKey: "label-transcribe-report-tab",
        content: (attemptId) => (
          <TranscribeChartBlock
            attemptId={attemptId}
            onOpenTranscribeDetail={onOpenTranscribeDetail}
            onAnalyticsData={setTranscribeAnalytics}
          />
        ),
      },
    ],
    [
      assignmentType,
      t,
      resultsChart,
      assignmentId,
      contextId,
      questionResults,
      entity,
      filteredComponents,
      onOpenTranscribeDetail,
      webinarRecords,
      credibilityVerdict,
    ]
  );

  const visibleTabs = useMemo(
    () => tabs.filter((tab) => {
      // Ограничение одним табом (напр. "actions" до генерации AI-отчёта).
      if (onlyTab) return tab.value === onlyTab;
      // Для external-задания «Интенсивность ответов» (qDensity) не применима.
      if (tab.value === "qDensity" && isExternalAssignment) {
        return false;
      }
      // Скрываем transcribe tab, если нет видео
      if (tab.value === 'transcribe' && tabDisabled?.transcribe) {
        return false;
      }
      return true;
    }),
    [tabs, tabDisabled, onlyTab, isExternalAssignment]
  );

  // Sync with initialTab only when it actually changes
  useEffect(() => {
    if (initialTab) {
      setActiveTab(resolveNextEnabledTab(getDefaultTab(initialTab)));
    }
  }, [initialTab]); // Removed resolveNextEnabledTab from dependencies

  // Auto-switch away from disabled tabs if tabDisabled status changes
  useEffect(() => {
    setActiveTab((prev) => resolveNextEnabledTab(prev));
  }, [tabDisabled, resolveNextEnabledTab]);

  if (!attemptId) return null;

  const currentHelpContent = helpContent[activeTab];
  const dialogTitleText = showHelp
    ? currentHelpContent.title
    : locale === "en"
      ? "AI analytics"
      : locale === "kz"
        ? "AI аналитикасы"
        : "AI аналитика";

  const renderTabsNavigation = () => {
    if (isMobile) {
      return (
        <Select
          value={activeTab}
          onValueChange={(value) => {
            const next = value as TabValue;
            if (tabDisabled?.[next]) return;
            setActiveTab(next);
          }}
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="z-[110]">
            {visibleTabs.map((tab) => (
              <SelectItem
                key={tab.value}
                value={tab.value}
                disabled={!!tabDisabled?.[tab.value]}
              >
                {t(tab.labelKey)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    return (
      <TabsList className="inline-flex items-center rounded-sm bg-[#F5F5F5] p-0.5 w-full">
        {visibleTabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            disabled={!!tabDisabled?.[tab.value]}
            className={TAB_TRIGGER_CLASSNAME}
          >
            {t(tab.labelKey)}
          </TabsTrigger>
        ))}
      </TabsList>
    );
  };

  const renderContent = () => {
    const summarySection = (
      activeTab !== 'transcribe' && !(activeTab === 'qDensity' && assignmentType === "external") && (
        <div className={`flex border rounded-md p-4 min-h-[60px] ${activeTab === 'actions' ? 'w-full md:w-1/2 text-sm items-start' : 'items-center'}`}>
          {activeTab === 'qDensity' || activeTab === 'actions' ? (
            getSummaryText() || <span className="text-gray-400 text-sm">{t('label-loading')}</span>
          ) : null}
        </div>
      )
    );

    return (
      <div className="flex-1 min-h-0 flex flex-col gap-4 pr-1 overflow-y-auto md:overflow-hidden">
        {activeTab === 'actions' ? (
          <div className="flex flex-col md:flex-row gap-4 md:flex-1 md:min-h-0">
            <div className="w-full md:w-1/2 border rounded-md p-4 md:min-h-0 md:overflow-y-auto">
              {visibleTabs.map((tab) => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className={TAB_CONTENT_CLASSNAME}
                >
                  {tab.content(attemptId)}
                </TabsContent>
              ))}
            </div>
            {summarySection}
          </div>
        ) : (
          <div className="flex flex-col gap-4 md:flex-1 md:min-h-0 md:overflow-y-auto">
            {visibleTabs.map((tab) => (
              <TabsContent
                key={tab.value}
                value={tab.value}
                className={TAB_CONTENT_CLASSNAME}
              >
                {tab.content(attemptId)}
              </TabsContent>
            ))}
            {summarySection}
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent
        className={`max-md:!max-w-none max-md:!w-full md:!max-w-[860px] md:!w-[860px] p-0 gap-0 overflow-visible z-[100] flex flex-col rounded-sm overflow-hidden ${
          activeTab === "transcribe" ? "max-h-[90vh]" : "max-md:h-full md:h-[500px]"
        }`}
        showCloseButton={false}
      >
        <CloseIcon
          className="h-5 w-5 text-[#70707D] cursor-pointer absolute right-4 top-3 z-10"
          onClick={handleClose}
        />

        {showHelp ? (
          <div className="p-4 md:p-10 flex flex-col gap-6 overflow-y-auto">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowHelp(false)}
                className="flex items-center gap-1 p-0 h-auto hover:bg-transparent"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <DialogTitle className="sr-only">
                {dialogTitleText}
              </DialogTitle>

              <h2 className="text-xl font-semibold text-[#1A1A1A]">{dialogTitleText}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {currentHelpContent.sections.map((section, index) => (
                <div
                  key={index}
                  className="border border-[#E5E5E5] rounded-lg p-6 flex flex-col gap-3"
                >
                  <h3 className="font-semibold text-base text-[#1A1A1A]">
                    {section.title}
                  </h3>
                  <p className="text-sm text-[#70707D] whitespace-pre-line leading-relaxed">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <Tabs
            value={activeTab}
            onValueChange={(value) => {
              const next = value as TabValue;
              if (tabDisabled?.[next]) return;
              setActiveTab(next);
            }}
            className="flex flex-col overflow-visible gap-4 p-4 md:p-10 h-full flex-1 mt-5"
          >
            <DialogTitle className="sr-only">
              {dialogTitleText}
            </DialogTitle>

            {renderTabsNavigation()}
            {renderContent()}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
});

export default AIChartsPanelModal;
